import { useTranslation } from "react-i18next"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { PanelRightOpen } from "lucide-react"
import { ParameterPanel } from "./parameter-panel"
import { ReportPreviewImage } from "./report-preview-image"
import type { ReportTreeItemData } from "@/lib/report-tree"
import type { Report } from "@/types/report"
import type { Parameter } from "@/types/parameter"

interface MobileParameterSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  report: Report | null
  onValueSet: (parameter: Parameter, report: Report) => void
  activeContext: "reportLibrary" | "reportList"
  selectedReportNode: ReportTreeItemData | null
}

export function MobileParameterSheet({
  open,
  onOpenChange,
  report,
  onValueSet,
  activeContext,
  selectedReportNode,
}: Readonly<MobileParameterSheetProps>) {
  const { t } = useTranslation()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="fixed bottom-4 right-4 z-50 h-12 w-12 rounded-full shadow-lg md:hidden"
        >
          <PanelRightOpen className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-3/4 max-w-sm p-0">
        <SheetHeader className="border-b p-3">
          <SheetTitle>{t("parameters.title")}</SheetTitle>
          {report && (
            <div className="space-y-0.5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold">
                  {report.displayName || report.name}
                </span>
                <span className="shrink-0 text-sm font-semibold text-muted-foreground">
                  {activeContext === "reportLibrary"
                    ? "Report Library"
                    : "Report List"}
                </span>
              </div>
              {report.reportVersion && (
                <p className="text-xs text-muted-foreground">
                  Version: {report.reportVersion}
                </p>
              )}
              {report.description && (
                <p className="line-clamp-3 text-xs text-muted-foreground">
                  {report.description}
                </p>
              )}
            </div>
          )}
        </SheetHeader>
        <div className="flex-1 overflow-auto">
          <ParameterPanel report={report} onValueSet={onValueSet} />
          {selectedReportNode?.type === "file" && (
            <div className="border-t p-3">
              <h5 className="text-lg font-semibold">
                {t("reportDetails.previewThumbnail")}
              </h5>
              <div className="mt-2">
                <ReportPreviewImage node={selectedReportNode} />
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
